/**
 * Socket Store
 *
 * Manages WebSocket connection state and provides hooks for real-time updates.
 * Integrates with the socket service from @soundbyte/services.
 */

import { create } from 'zustand';
import { useEffect, useCallback } from 'react';
import {
  socketService,
  type GenerationStatusEvent,
  type GenerationProgressEvent,
  type GenerationCompletedEvent,
  type NotificationEvent,
  type RequestUpdateEvent,
} from '@soundbyte/services';

interface SocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
}

export const useSocketStore = create<SocketState>((set) => ({
  isConnected: false,
  isConnecting: false,
  error: null,

  connect: async () => {
    set({ isConnecting: true, error: null });
    try {
      await socketService.connect();
      set({ isConnected: true, isConnecting: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to connect',
        isConnecting: false,
        isConnected: false,
      });
    }
  },

  disconnect: () => {
    socketService.disconnect();
    set({ isConnected: false, isConnecting: false });
  },

  setConnected: (connected) => set({ isConnected: connected }),
  setError: (error) => set({ error }),
}));

/**
 * Hook to manage socket connection lifecycle
 * Call this in your root component to auto-connect when authenticated
 */
export function useSocketConnection(shouldConnect: boolean = true) {
  const { isConnected, isConnecting, error, connect, disconnect } = useSocketStore();

  useEffect(() => {
    if (shouldConnect && !isConnected && !isConnecting) {
      connect();
    }

    return () => {
      // Optionally disconnect on unmount
      // disconnect();
    };
  }, [shouldConnect, isConnected, isConnecting, connect]);

  return { isConnected, isConnecting, error, disconnect };
}

/**
 * Hook to subscribe to generation status updates
 */
export function useGenerationStatus(
  generationId: string | null,
  onStatus: (event: GenerationStatusEvent) => void
) {
  useEffect(() => {
    if (!generationId) return;

    // Subscribe to this generation's updates
    socketService.subscribeToGeneration(generationId);

    // Listen for status updates
    const unsubscribe = socketService.onGenerationStatus((event) => {
      if (event.generationId === generationId) {
        onStatus(event);
      }
    });

    return () => {
      socketService.unsubscribeFromGeneration(generationId);
      unsubscribe();
    };
  }, [generationId, onStatus]);
}

/**
 * Hook to subscribe to generation progress updates
 */
export function useGenerationProgress(
  generationId: string | null,
  onProgress: (event: GenerationProgressEvent) => void
) {
  useEffect(() => {
    if (!generationId) return;

    const unsubscribe = socketService.onGenerationProgress((event) => {
      if (event.generationId === generationId) {
        onProgress(event);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [generationId, onProgress]);
}

/**
 * Hook to subscribe to generation completed events
 */
export function useGenerationCompleted(
  generationId: string | null,
  onCompleted: (event: GenerationCompletedEvent) => void
) {
  useEffect(() => {
    if (!generationId) return;

    const unsubscribe = socketService.onGenerationCompleted((event) => {
      if (event.generationId === generationId) {
        onCompleted(event);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [generationId, onCompleted]);
}

/**
 * Hook to subscribe to real-time notifications via socket
 */
export function useSocketNotifications(onNotification: (event: NotificationEvent) => void) {
  useEffect(() => {
    const unsubscribe = socketService.onNotification(onNotification);
    return () => {
      unsubscribe();
    };
  }, [onNotification]);
}

/**
 * Hook to subscribe to request updates (Originals and Sonic)
 */
export function useRequestUpdates(
  requestId: string | null,
  requestType: 'soundbyte' | 'sonic',
  onUpdate: (event: RequestUpdateEvent) => void
) {
  useEffect(() => {
    if (!requestId) return;

    socketService.subscribeToRequest(requestId, requestType);

    const unsubscribe = socketService.onRequestUpdate((event) => {
      if (event.requestId === requestId) {
        onUpdate(event);
      }
    });

    return () => {
      socketService.unsubscribeFromRequest(requestId);
      unsubscribe();
    };
  }, [requestId, requestType, onUpdate]);
}

/**
 * Combined hook for tracking a generation's full lifecycle
 */
export function useGenerationTracking(
  generationId: string | null,
  callbacks: {
    onStatus?: (event: GenerationStatusEvent) => void;
    onProgress?: (event: GenerationProgressEvent) => void;
    onCompleted?: (event: GenerationCompletedEvent) => void;
  }
) {
  const handleStatus = useCallback(
    (event: GenerationStatusEvent) => callbacks.onStatus?.(event),
    [callbacks.onStatus]
  );

  const handleProgress = useCallback(
    (event: GenerationProgressEvent) => callbacks.onProgress?.(event),
    [callbacks.onProgress]
  );

  const handleCompleted = useCallback(
    (event: GenerationCompletedEvent) => callbacks.onCompleted?.(event),
    [callbacks.onCompleted]
  );

  useGenerationStatus(generationId, handleStatus);
  useGenerationProgress(generationId, handleProgress);
  useGenerationCompleted(generationId, handleCompleted);
}
